import { DaysOffEditor } from "@/components/work/DaysOff";
import { useScrum } from "@/components/work/SprintBits";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useChangePassword, useLogout, useLogoutAll, useMe, useUpdateMe } from "@/api/hooks";
import type { Locale } from "@/api/types/Locale";
import type { ThemeMode } from "@/api/types/ThemeMode";
import { AreaTabs } from "@/components/AreaTabs";
import { useToast } from "@/components/Toast";
import { Button, Field, Input, PageHeader, RoleChip, Segmented, Select } from "@/components/ui";
import { LOCALES } from "@/i18n";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { CliTokensSection } from "./CliTokensSection";
import { NotificationChannelsSection } from "./NotificationChannelsSection";

/** The profile's tabs (`/profile/<section>`; Account is `/profile`). */
const SECTIONS = ["notifications", "days-off", "security", "cli"] as const;

export function ProfilePage() {
  const { t } = useTranslation();
  const me = useMe();
  const param = useParams().section;
  const cli = me.role !== "viewer";
  const scrum = useScrum();
  const section = param === undefined ? "account" : SECTIONS.find((s) => s === param && (s !== "cli" || cli));
  if (!section) return <NotFoundPage />;

  return (
    <div className="flex flex-col gap-5 p-6">
      <PageHeader title={t("profile.title")} />
      <AreaTabs
        label={t("profile.title")}
        tabs={[
          { to: "/profile", label: t("profile.account"), end: true },
          { to: "/profile/notifications", label: t("notifications.channels.title") },
          ...(scrum ? [{ to: "/profile/days-off", label: t("sprints.daysOff.title") }] : []),
          { to: "/profile/security", label: t("profile.security") },
          ...(cli ? [{ to: "/profile/cli", label: t("tokens.title") }] : []),
        ]}
      />
      <div className="flex max-w-3xl flex-col gap-5">
        {section === "account" && (
          <>
            <AccountSection />
            <PreferencesSection />
          </>
        )}
        {section === "notifications" && <NotificationChannelsSection />}
        {section === "days-off" && (
          <Section title={t("sprints.daysOff.title")}>
            <DaysOffEditor userId={null} />
          </Section>
        )}
        {section === "security" && (
          <>
            <PasswordSection />
            <SessionsSection />
          </>
        )}
        {section === "cli" && <CliTokensSection />}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card flex flex-col gap-4 p-5">
      <h2 className="m-0 label-caps">{title}</h2>
      {children}
    </section>
  );
}

function AccountSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const update = useUpdateMe();
  const [name, setName] = useState(me.display_name);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { display_name: name.trim(), locale: null, theme_mode: null },
      { onSuccess: () => toast.success(t("profile.saved")), onError: (err) => toast.error(err) },
    );
  };

  return (
    <Section title={t("profile.account")}>
      <dl className="m-0 grid grid-cols-[max-content_1fr] items-center gap-x-6 gap-y-2 text-sm">
        <dt className="text-muted">{t("profile.username")}</dt>
        <dd className="m-0 font-mono">{me.username}</dd>
        <dt className="text-muted">{t("profile.role")}</dt>
        <dd className="m-0">
          <RoleChip role={me.role} />
        </dd>
      </dl>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3" noValidate>
        <div className="min-w-[240px] flex-1">
          <Field label={t("profile.displayName")}>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />}
          </Field>
        </div>
        <Button
          type="submit"
          variant="primary"
          loading={update.isPending}
          disabled={!name.trim() || name.trim() === me.display_name}
        >
          {t("common.save")}
        </Button>
      </form>
    </Section>
  );
}

function PreferencesSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const update = useUpdateMe();

  // Saved immediately; the shell applies the new language/theme from the session.
  const save = (body: { locale?: Locale; theme_mode?: ThemeMode }) =>
    update.mutate(
      { display_name: null, locale: body.locale ?? null, theme_mode: body.theme_mode ?? null },
      { onError: (err) => toast.error(err) },
    );

  return (
    <Section title={t("profile.preferences")}>
      <div className="flex flex-wrap gap-6">
        <div className="min-w-[200px]">
          <Field label={t("profile.language")}>
            {(p) => (
              <Select {...p} value={me.locale} onChange={(e) => save({ locale: e.target.value as Locale })}>
                {LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {t(`locales.${l}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-2">{t("profile.theme")}</span>
          <Segmented<ThemeMode>
            label={t("profile.theme")}
            value={me.theme_mode}
            onChange={(theme_mode) => save({ theme_mode })}
            options={[
              { value: "light", label: t("theme.light") },
              { value: "dark", label: t("theme.dark") },
              { value: "system", label: t("theme.system") },
            ]}
          />
        </div>
      </div>
    </Section>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = repeat.length > 0 && repeat !== next;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (tooShort || mismatch) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          toast.success(t("profile.passwordChanged"));
          setCurrent("");
          setNext("");
          setRepeat("");
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Section title={t("profile.changePassword")}>
      <form onSubmit={submit} className="flex max-w-md flex-col gap-4" noValidate>
        <input type="text" autoComplete="username" value={me.username} readOnly hidden />
        <Field label={t("profile.currentPassword")}>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          )}
        </Field>
        <Field
          label={t("profile.newPassword")}
          hint={t("profile.passwordHint")}
          error={tooShort ? t("profile.passwordTooShort") : undefined}
        >
          {(p) => (
            <Input {...p} type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          )}
        </Field>
        <Field label={t("profile.confirmPassword")} error={mismatch ? t("profile.passwordMismatch") : undefined}>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>
        <div>
          <Button
            type="submit"
            variant="primary"
            loading={change.isPending}
            disabled={!current || next.length < 12 || next !== repeat}
          >
            {t("profile.changePassword")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function SessionsSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const logoutAll = useLogoutAll();
  const logout = useLogout();

  return (
    <Section title={t("profile.sessions")}>
      <p className="m-0 text-sm text-text-2">{t("profile.logoutOthersHint")}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          loading={logoutAll.isPending}
          onClick={() =>
            logoutAll.mutate(undefined, {
              onSuccess: () => toast.success(t("profile.loggedOutOthers")),
              onError: (err) => toast.error(err),
            })
          }
        >
          {t("profile.logoutOthers")}
        </Button>
        <Button variant="danger" icon="logout" loading={logout.isPending} onClick={() => logout.mutate()}>
          {t("profile.logout")}
        </Button>
      </div>
    </Section>
  );
}
